export interface IDashboard {
    id?: string;
    organization?: string;
    resource?: string;
    name?: string;
    description?: string;
    storefrontId?: string;
    widgets?: IDashboardWidget[];
    createdBy?: string;
    updatedBy?: string;
    deleted?: boolean;
    lastUpdated?: string;
    createdAt?: string;
}

export interface IDashboardWidget {
    widgetId?: string;
    widgetType?: string;
    x: number;
    y: number;
    w: number;
    h: number;
    configuration?: IWidgetConfiguration;
}

export interface IDashboardCreateRequest {
    name: string;
    description?: string;
    storefrontId?: string;
    widgets?: IDashboardWidget[];
}

export interface IDashboardUpdateRequest {
    name?: string;
    description?: string;
    widgets?: IDashboardWidget[];
}

// Backend ReportingWidget (V_7.0)
export type WidgetDisplayType = 'Metric' | 'LineChart' | 'BarChart' | 'PieChart' | 'Table';

export interface IReportingWidget {
    resource: string;
    widgetType: string;
    displayType: WidgetDisplayType;
    description: string;
    hasTimeframeOptions?: boolean;
    supportedIntervals?: string[];
    metricOptions?: string[];
    filterOptions?: Record<string, string[]>;
}

// Mirrors backend ReportingWidgetDataRequest
export type TimeframeType = 'days' | 'months' | 'years' | 'ytd' | 'mtd' | 'lastMonth';

export interface IWidgetConfiguration {
    timeframe?: TimeframeType;
    timeframeValue?: number;
    interval?: string;
    metric?: string;
    resourceFilter?: string;
    // Format: "Key=Value and Key=Value"
    filter?: string;
}

export interface IWidgetDataRequest extends IWidgetConfiguration {
    from?: string;
    to?: string;
}

export interface IWidgetDataResponse {
    widgetType?: string;
    dataPoints?: IWidgetDataPoint[];
    totalValue?: number;
    totalCount?: number;
    rows?: IWidgetTableRow[];
}

export interface IWidgetDataPoint {
    label?: string;
    value: number;
    count: number;
}

export interface IWidgetTableRow {
    id?: string;
    date?: string;
    data?: Record<string, string>;
}

// Client-side sizing defaults keyed by display type — the backend does not
// ship sizing info, so we apply sensible defaults when adding a widget.
export interface IWidgetSizing {
    defaultW: number;
    defaultH: number;
    minW: number;
    minH: number;
    maxW?: number;
    maxH?: number;
}

export const WIDGET_SIZING_DEFAULTS: Record<WidgetDisplayType, IWidgetSizing> = {
    Metric:    { defaultW: 2, defaultH: 1, minW: 2, minH: 1, maxW: 3, maxH: 2 },
    LineChart: { defaultW: 6, defaultH: 3, minW: 4, minH: 2 },
    BarChart:  { defaultW: 6, defaultH: 3, minW: 4, minH: 2 },
    PieChart:  { defaultW: 4, defaultH: 3, minW: 3, minH: 2 },
    Table:     { defaultW: 6, defaultH: 3, minW: 4, minH: 2 },
};

export function getWidgetSizing(displayType?: string): IWidgetSizing {
    if (displayType && displayType in WIDGET_SIZING_DEFAULTS) {
        return WIDGET_SIZING_DEFAULTS[displayType as WidgetDisplayType];
    }
    return WIDGET_SIZING_DEFAULTS.BarChart;
}

// Pretty display name for a widget type enum value
export function getWidgetDisplayName(widgetType?: string): string {
    if (!widgetType) return 'Widget';
    // Insert spaces before capital letters: "SalesOverTime" -> "Sales Over Time"
    return widgetType.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function widgetHasConfiguration(widget?: IReportingWidget): boolean {
    if (!widget) return false;
    return !!(widget.hasTimeframeOptions
        || (widget.metricOptions && widget.metricOptions.length > 0)
        || (widget.filterOptions && Object.keys(widget.filterOptions).length > 0));
}
